import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  AccessTime,
  Timer,
  CalendarToday,
  TrendingUp,
  AutoGraph,
  LocationOn,
  Insights,
  CloudDone,
  Schedule,
  Assessment,
  PeopleAlt,
  MonetizationOn
} from '@mui/icons-material';

const PageContainer = styled.div`
  padding-top: 0;
  min-height: 100vh;
`;

const Hero = styled.div`
  background: linear-gradient(
    150deg,
    #ff80b5 0%,
    #9089fc 20%,
    #ff80b5 40%,
    #40c9ff 60%,
    #a5fecb 80%,
    #9089fc 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 16rem 2rem 8rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    filter: blur(60px);
    opacity: 0.6;
  }

  h1 {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    position: relative;
    z-index: 2;
  }

  p {
    font-size: 1.25rem;
    max-width: 800px;
    margin: 0 auto;
    opacity: 0.9;
    line-height: 1.6;
    position: relative;
    z-index: 2;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const FeatureSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;
  padding: 6rem 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  &:nth-child(even) {
    direction: rtl;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    direction: ltr;
    padding: 4rem 0;
  }
`;

const TextContent = styled(motion.div)`
  direction: ltr;
  
  h3 {
    font-size: 1rem;
    color: #4f46e5;
    margin-bottom: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  h2 {
    font-size: 2.25rem;
    color: #1f2937;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    line-height: 1.2;
  }

  p {
    color: #4b5563;
    line-height: 1.8;
    margin-bottom: 2rem;
    font-size: 1.125rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 2rem 0;
  }

  li {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.25rem;
    color: #4b5563;
    font-size: 1.125rem;

    svg {
      color: #4f46e5;
      margin-top: 0.25rem;
    }
  }
`;

const ImageContainer = styled(motion.div)`
  direction: ltr;
  
  img {
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  }
`;

const features = [
  {
    icon: <Timer sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "Automated Time Tracking",
    subtitle: "Effortless Recording",
    description: "Automatically track time spent on jobs and projects with smart detection and GPS integration. No more manual entries or forgotten timesheets.",
    benefits: [
      "One-click time tracking",
      "Automatic job detection",
      "GPS location verification",
      "Mobile app support"
    ],
    image: "/images/features/time-tracking.jpg"
  },
  {
    icon: <Schedule sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "Smart Scheduling",
    subtitle: "Intelligent Planning",
    description: "Plan and manage work schedules with intelligent automation. Optimize staff allocation and ensure proper coverage for all shifts.",
    benefits: [
      "AI-powered schedule optimization",
      "Shift pattern management",
      "Break time tracking",
      "Overtime monitoring"
    ],
    image: "/images/features/smart-scheduling.jpg"
  },
  {
    icon: <Assessment sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "Payroll Integration",
    subtitle: "Seamless Processing",
    description: "Automatically calculate wages, overtime, and benefits based on accurate time data. Integrate directly with your payroll system.",
    benefits: [
      "Automatic wage calculation",
      "Overtime processing",
      "Leave management",
      "Payroll system integration"
    ],
    image: "/images/features/payroll-integration.jpg"
  },
  {
    icon: <Insights sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "Time Analytics",
    subtitle: "Performance Insights",
    description: "Gain valuable insights into labor costs, productivity, and project efficiency with comprehensive time analytics.",
    benefits: [
      "Labor cost analysis",
      "Productivity tracking",
      "Project time insights",
      "Custom reporting tools"
    ],
    image: "/images/features/time-analytics.jpg"
  }
];

const SmartTimesheets = () => {
  return (
    <PageContainer>
      <Hero>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Smart Timesheets & Scheduling
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Automate your time tracking and scheduling with intelligent timesheets. Boost productivity, reduce errors, and streamline payroll processing.
        </motion.p>
      </Hero>
      
      <Content>
        {features.map((feature, index) => (
          <FeatureSection key={feature.title}>
            <TextContent
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3>{feature.subtitle}</h3>
              <h2>{feature.icon} {feature.title}</h2>
              <p>{feature.description}</p>
              <ul>
                {feature.benefits.map((benefit, i) => (
                  <li key={i}>
                    <TrendingUp sx={{ fontSize: 20 }} />
                    {benefit}
                  </li>
                ))}
              </ul>
            </TextContent>
            <ImageContainer
              initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img src={feature.image} alt={feature.title} />
            </ImageContainer>
          </FeatureSection>
        ))}
      </Content>
    </PageContainer>
  );
};

export default SmartTimesheets;
